import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { RequestStatus } from 'clickNgo/configs/service/common'
import { Nullable } from 'cng-common/models/utils'
import { createBlankAssortmentGroup } from 'partner360/features/assortment/helpers/createBlankAssortmentGroup'
import { createBlankAssortmentItem } from 'partner360/widgets/assortmentTab/context/helpers/createBlankAssortmentItem'

import { P360_ASSORTMENT_NAMESPACE } from './constants'
import { resetAssortmentLoadableState } from './helpers/resetAssortmentLoadableState'
import { initialState } from './initialState'
import { IAssortmentCategory } from './models/data/IAssortmentCategory'
import { IAssortmentGroup } from './models/data/IAssortmentGroup'
import { IAssortmentItem } from './models/data/IAssortmentItem'
import { TAddBlankGroupPayload } from './models/IAddAssortmentGroup'
import {
  requestAssortmentCategories,
  requestAssortmentGroups,
  requestAssortmentItems,
  requestRemoveAssortmentGroup,
  requestRemoveAssortmentItem,
  requestSaveAssortmentGroupsSortOrder,
  requestSaveAssortmentItemsSortOrder,
} from './thunks'
import { requestSaveAssortmentGroup } from './thunks/requestSaveAssortmentGroup'

const assortmentSlice = createSlice({
  name: P360_ASSORTMENT_NAMESPACE,
  initialState,
  reducers: {
    changeItemGroup(state, action: PayloadAction<{ itemId: number; newGroupId: number }>) {
      const { itemId, newGroupId } = action.payload
      const itemIndex = state.items.data.findIndex((it) => it.EntryId === itemId)
      state.items.data[itemIndex].AssortmentGroupId = newGroupId
    },
    changeItemOrder(state, action: PayloadAction<{ itemId: number; targetIndex: number }>) {
      const { itemId, targetIndex } = action.payload
      const data = state.items.data
      const dragIndex = data.findIndex((it) => it.EntryId === itemId)
      const [dragItem] = data.splice(dragIndex, 1)
      data.splice(targetIndex, 0, dragItem)
      state.items.data = data.map((it, i) => ({
        ...it,
        SortOrder: i + 1,
      }))
    },
    changeGroupOrder(state, action: PayloadAction<{ itemId: number; targetIndex: number }>) {
      const { itemId, targetIndex } = action.payload
      const data = state.groups.data
      const dragIndex = data.findIndex((it) => it.EntryId === itemId)
      const [dragItem] = data.splice(dragIndex, 1)
      data.splice(targetIndex, 0, dragItem)
      state.groups.data = data.map((it, i) => ({
        ...it,
        SortOrder: i + 1,
      }))
    },
    addBlankItem(state, action: PayloadAction<number>) {
      const assortmentGroupId = action.payload
      const newEntries = state.items.data.filter((it) => it.EntryId < 0).map((it) => it.EntryId)
      const nextNewEntry = !newEntries.length ? -1 : Math.min(...newEntries) - 1
      const newItem = createBlankAssortmentItem({
        assortmentGroupId: assortmentGroupId,
        entryId: nextNewEntry,
      })
      newItem.SortOrder = state.items.data.length + 1
      state.items.data.push(newItem)

      // mark parent as changed
      const groupIndex = state.groups.data.findIndex((it) => it.EntryId === assortmentGroupId)
      state.groups.data[groupIndex].isChanged = true
    },
    addBlankGroup(state, action: TAddBlankGroupPayload) {
      const newGroup = createBlankAssortmentGroup(action.payload)
      newGroup.SortOrder = state.groups.data.length + 1
      state.groups.data.push(newGroup)
      state.editingGroupId = -1

      const newEntries = state.items.data.filter((it) => it.EntryId < 0).map((it) => it.EntryId)
      const nextNewEntry = !newEntries.length ? -1 : Math.min(...newEntries) - 1
      const newItem = createBlankAssortmentItem({
        assortmentGroupId: newGroup.EntryId,
        entryId: nextNewEntry,
      })
      newItem.SortOrder = state.items.data.length + 1
      state.items.data.push(newItem)
    },
    changeEditingGroupId(state, action: PayloadAction<number>) {
      const groupId = action.payload
      state.editingGroupId = groupId
    },
    updateAssortmentGroup(state, action: PayloadAction<IAssortmentGroup>) {
      const updateItem = {
        ...action.payload,
        isChanged: true,
      }
      const index = state.groups.data.findIndex((it) => it.EntryId === updateItem.EntryId)
      state.groups.data[index] = updateItem
    },
    cancelEditingGroup(state) {
      state.editingGroupId = null
      state.groups.data = state.groups.originalData
      state.items.data = state.items.originalData
    },
    updateAssortmentItem(state, action: PayloadAction<IAssortmentItem>) {
      const updateItem = {
        ...action.payload,
        isChanged: true,
      }
      const itemIndex = state.items.data.findIndex((it) => it.EntryId === updateItem.EntryId)
      state.items.data[itemIndex] = updateItem

      // mark parent as changed
      const groupIndex = state.groups.data.findIndex(
        (it) => it.EntryId === updateItem.AssortmentGroupId
      )
      state.groups.data[groupIndex].isChanged = true
    },
    changeAssortmentSidePanelCollapsedState(state) {
      state.isCollapsed = !state.isCollapsed
    },
    changeSelectedCategory(state, action: PayloadAction<Nullable<IAssortmentCategory>>) {
      state.selectedCategory = action.payload
      resetAssortmentLoadableState(state.groups)
      resetAssortmentLoadableState(state.items)
    },
    changeAssortmentCategory(state, action: PayloadAction<IAssortmentCategory>) {
      const updatedCategory = action.payload
      const index = state.categories.originalData.findIndex(
        (it) => it.EntryId === updatedCategory.EntryId
      )

      if (index !== -1) {
        state.categories.originalData[index] = updatedCategory
      }

      if (state.selectedCategory?.EntryId === updatedCategory.EntryId) {
        state.selectedCategory = updatedCategory
        resetAssortmentLoadableState(state.groups)
        resetAssortmentLoadableState(state.items)
      }
    },
  },
  extraReducers(builder) {
    builder
      .addCase(requestAssortmentCategories.pending, (state) => {
        state.categories.status = RequestStatus.PENDING
      })
      .addCase(requestAssortmentCategories.rejected, (state, action) => {
        state.categories.status = RequestStatus.REJECTED
        if (action.error.message) {
          state.categories.message = action.error.message
        }
      })
      .addCase(requestAssortmentCategories.fulfilled, (state, action) => {
        state.categories.status = RequestStatus.FULFILLED
        state.categories.originalData = action.payload
        state.categories.data = action.payload
        state.selectedCategory = action.payload[0]
      })

      .addCase(requestAssortmentItems.pending, (state) => {
        state.items.status = RequestStatus.PENDING
      })
      .addCase(requestAssortmentItems.rejected, (state, action) => {
        state.items.status = RequestStatus.REJECTED
        if (action.error.message) {
          state.items.message = action.error.message
        }
      })
      .addCase(requestAssortmentItems.fulfilled, (state, action) => {
        state.items.status = RequestStatus.FULFILLED
        state.items.originalData = action.payload
        state.items.data = action.payload
      })

      .addCase(requestAssortmentGroups.pending, (state) => {
        state.groups.status = RequestStatus.PENDING
      })
      .addCase(requestAssortmentGroups.rejected, (state, action) => {
        state.groups.status = RequestStatus.REJECTED
        if (action.error.message) {
          state.groups.message = action.error.message
        }
      })
      .addCase(requestAssortmentGroups.fulfilled, (state, action) => {
        state.groups.status = RequestStatus.FULFILLED
        state.groups.originalData = action.payload
        state.groups.data = action.payload
      })

      .addCase(requestRemoveAssortmentGroup.pending, (state) => {
        state.groups.status = RequestStatus.PENDING
      })
      .addCase(requestRemoveAssortmentGroup.rejected, (state, action) => {
        state.groups.status = RequestStatus.REJECTED
        if (action.error.message) {
          state.groups.message = action.error.message
        }
      })
      .addCase(requestRemoveAssortmentGroup.fulfilled, (state, action) => {
        state.groups.status = RequestStatus.FULFILLED
        const groupId = action.payload
        if (groupId) {
          const index = state.groups.originalData.findIndex((it) => it.EntryId === groupId)
          state.groups.originalData.splice(index, 1)
          state.groups.data = state.groups.originalData
        }
      })
      .addCase(requestRemoveAssortmentItem.pending, (state) => {
        state.items.status = RequestStatus.PENDING
      })
      .addCase(requestRemoveAssortmentItem.rejected, (state, action) => {
        state.items.status = RequestStatus.REJECTED
        if (action.error.message) {
          state.items.message = action.error.message
        }
      })
      .addCase(requestRemoveAssortmentItem.fulfilled, (state, action) => {
        state.items.status = RequestStatus.FULFILLED
        const entryId = action.payload
        if (entryId) {
          const index = state.items.originalData.findIndex((it) => it.EntryId === entryId)
          state.items.originalData.splice(index, 1)
          state.items.data = state.items.originalData
        }
      })
      .addCase(requestSaveAssortmentGroup.pending, (state) => {
        state.groups.status = RequestStatus.PENDING
      })
      .addCase(requestSaveAssortmentGroup.rejected, (state, action) => {
        state.groups.status = RequestStatus.REJECTED
        if (action.error.message) {
          state.groups.message = action.error.message
        }
      })
      .addCase(requestSaveAssortmentGroup.fulfilled, (state, action) => {
        state.groups.status = RequestStatus.FULFILLED
      })

      .addCase(requestSaveAssortmentGroupsSortOrder.pending, (state) => {
        state.groups.status = RequestStatus.PENDING
      })
      .addCase(requestSaveAssortmentGroupsSortOrder.rejected, (state) => {
        state.groups.status = RequestStatus.REJECTED
      })
      .addCase(requestSaveAssortmentGroupsSortOrder.fulfilled, (state, action) => {
        state.groups.status = RequestStatus.FULFILLED
      })

      .addCase(requestSaveAssortmentItemsSortOrder.pending, (state) => {
        state.items.status = RequestStatus.PENDING
      })
      .addCase(requestSaveAssortmentItemsSortOrder.rejected, (state) => {
        state.items.status = RequestStatus.REJECTED
      })
      .addCase(requestSaveAssortmentItemsSortOrder.fulfilled, (state, action) => {
        state.items.status = RequestStatus.FULFILLED
      })
  },
})

export default assortmentSlice.reducer

export const {
  changeItemGroup,
  changeItemOrder,
  changeGroupOrder,
  addBlankItem,
  addBlankGroup,
  updateAssortmentItem,
  updateAssortmentGroup,
  cancelEditingGroup,
  changeEditingGroupId,
  changeSelectedCategory,
  changeAssortmentSidePanelCollapsedState,
  changeAssortmentCategory,
} = assortmentSlice.actions
